import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Observable, take } from 'rxjs';
import { HelpersService } from './helpers.service';

export interface LoginResponse {
  data?: any;
  statusCode: number;
  message: string;
  errors?: any;
}

export interface LoginPayload {
  username: string;
  password: string;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  protected jwtToken = 'jjnmc-token';
  protected user = 'jjnmc-user';
  protected baseUrl = '/api/auth';

  constructor(
    private http: HttpClient,
    private router: Router,
    private jwtHelper: JwtHelperService,
    private helpers: HelpersService,
  ) {}

  auth({ username, password }: LoginPayload): Promise<LoginResponse> {
    const url = this.baseUrl + '/login';
    return new Promise((resolve, reject) => {
      this.http.post<LoginResponse>(url, { username, password }).subscribe({
        next: (data) => {
          return resolve(data);
        },
        error: (err) => {
          return reject(err.error);
        },
      });
    });
  }

  logout(): void {
    if (!this.getLoggedUser()) return;
    const url = this.baseUrl + '/logout';
    if (this.getJwtToken()) {
      this.http.post<any>(url, { token: this.getJwtToken() }).pipe(take(1)).subscribe({
        next: (data) => {
          this.removeToken();
          this.router.navigate(['/login']);
        },
        error: (err) => {
          this.removeToken();
          this.router.navigate(['/login']);
        },
      });
    }
  }

  getJwtToken(): string {
    return localStorage.getItem(this.jwtToken) ?? '';
  }

  removeToken(): void {
    localStorage.removeItem(this.jwtToken);
    localStorage.removeItem(this.user);
  }

  getRoleFromToken() {
    if (!this.getJwtToken()) return null
    const tokenData = JSON.parse(atob(this.getJwtToken().split('.')[1]))
    return tokenData;
  }

  getLoggedUser(): any {
    let data = JSON.parse(localStorage.getItem(this.user)!);
    return data;
  }

  setJwtToken(token: string): void {
    localStorage.setItem(this.jwtToken, token);
  }

  setUser(user: string): void {
    localStorage.setItem(this.user, user);
  }

  forgotPassword(email: string): Observable<any> {
    const url = this.baseUrl + '/forgot-password';
    const body = { credential: email };
    return this.http.post<any>(url, body);
  }

  restorePassword(data: any): Observable<any> {
    const url = this.baseUrl + '/reset-password';
    return this.http.post<any>(url, data);
  }

  isAuthenticated(): boolean {
    return !this.jwtHelper.isTokenExpired(this.getJwtToken());
  }

  sameRole(role: any[], jwtRole: any[]): boolean {
    const roleString = role.sort().join('')
    const jwtRoleString = role.sort().join('')
    if (roleString != jwtRoleString) {
      this.helpers.sbError("Sesi Anda telah berakhir")
    }
    return roleString == jwtRoleString;
  }

  isSysAdmin(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('SYS')
  }

  isFinance(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('FIN')
  }

  isApprover(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('APV')
  }

  isManagement(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('MNG')
  }

  isAdminIntegration(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('ADI')
  }

  isHrd(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('HRD')
  }

  isPic(user: any) {
    const role = user.userRoles.map((item: any) => {return item.role.code})
    return role.includes('PIC')
  }

  hasAllReportAccess(user: any): boolean {
    const role = user.userRoles.map((item: any) => item.role.code);
    return role.includes('SYS') || role.includes('FIN');
  }

  hasTeamReportAccess(user: any): boolean {
    const role = user.userRoles.map((item: any) => item.role.code);
    return role.includes('MNG');
  }

  canAccessReport(user: any, positions: any[] = []): boolean {
    if (this.hasAllReportAccess(user) || this.hasTeamReportAccess(user)) {
      return true;
    }

    const positionName = user.position ?? '';
    const position = positions.find((item: any) => item.name === positionName);
    return ['all', 'team'].includes(position?.reportScope);
  }
}
